import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Alert, AlertChannel, AlertSeverity, AlertStatus, HazardType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const EOC_ROLES = ['DUTY_OFFICER', 'EOC_COMMANDER'];
const HAZARDS: HazardType[] = ['CYCLONE', 'FLOOD', 'TSUNAMI', 'LANDSLIDE', 'MARINE', 'HEALTH'];
const SEVERITIES: AlertSeverity[] = ['MINOR', 'MODERATE', 'SEVERE', 'EXTREME'];
const CHANNELS: AlertChannel[] = ['WHATSAPP', 'SMS', 'EMAIL', 'PUSH', 'SIREN', 'PORTAL_BANNER', 'APP_BANNER'];
const STATUSES: AlertStatus[] = ['DRAFT', 'PENDING_COSIGN', 'ACTIVE', 'CANCELLED', 'EXPIRED'];
const DISTRICTS_OR_ALL = [
  'ALL',
  'Beau Vallon',
  'Anse Royale',
  'Baie Lazare',
  'Anse Boileau',
  'Takamaka',
  'Port Glaud',
  'Cascade',
  'Au Cap',
  'Anse Aux Pins',
  'English River',
  'Mont Buxton',
  'Plaisance',
  'St Louis',
  'Roche Caiman',
  'Bel Air',
  'Mont Fleuri',
  'Glacis',
  'Bel Ombre',
  'Anse Etoile',
  'Machabee',
  'Pointe Larue',
  'Grand Anse Mahe',
  'Grand Anse Praslin',
  'Baie Ste Anne',
];

/**
 * CAP alert issuance. Severity EXTREME is the platform's single most
 * privileged action (spec: "hardware-key auth"): publish moves it to
 * PENDING_COSIGN instead of straight to ACTIVE, and a SECOND, DIFFERENT
 * EOC_COMMANDER must cosign before it broadcasts (SeyNotify/siren/OpenGov
 * Gazette) — the dual-auth/four-eyes step is deliberately its own distinct
 * stage in this screen, not folded into "publish".
 */
export function Alerts() {
  const { persona } = useAuth();
  const isEoc = persona ? persona.roles.some((r) => EOC_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [hazard, setHazard] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (hazard) params.set('hazard', hazard);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m36/alerts?${params.toString()}`;
  }, [status, hazard, appliedQ]);
  const listQ = useApiQuery<Page<Alert>>(isEoc ? path : null);

  return (
    <div>
      <PageHeader
        title="Alerts"
        subtitle="CAP-standard hazard alerts — DRAFT -> ACTIVE (or PENDING_COSIGN for EXTREME, dual-auth) -> CANCELLED/EXPIRED (M36)."
        actions={
          isEoc ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Draft alert
            </button>
          ) : undefined
        }
      />

      {!isEoc ? <RoleNotice roles={EOC_ROLES} /> : null}

      {isEoc ? (
        <>
          <form
            className="mb-4 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-56" placeholder="Ref or headline…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={hazard} onChange={(e) => setHazard(e.target.value)}>
              <option value="">All hazards</option>
              {HAZARDS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No alerts match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Hazard</th>
                    <th className="table-head">Severity</th>
                    <th className="table-head">Headline</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((a) => (
                    <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{a.refNo}</td>
                      <td className="table-cell">
                        <StatusBadge status={a.hazard} />
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={a.severity} />
                      </td>
                      <td className="table-cell text-xs text-slate-600">{a.headline}</td>
                      <td className="table-cell">
                        <StatusBadge status={a.effectiveStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? (
        <AlertDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={(id) => {
            setShowCreate(false);
            listQ.reload();
            setSelectedId(id);
          }}
        />
      ) : null}
    </div>
  );
}

function AlertDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { persona } = useAuth();
  const q = useApiQuery<Alert>(`/api/v1/m36/alerts/${id}`);
  const a = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const isCommander = persona?.roles.includes('EOC_COMMANDER') ?? false;
  const isEoc = persona ? persona.roles.some((r) => EOC_ROLES.includes(r)) : false;
  const isSameOfficerAsIssuer = Boolean(a && persona?.sub && a.issuedBy === persona.sub);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={a ? `${a.refNo} — ${a.headline}` : 'Alert'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-4 text-sm">
          {a.severity === 'EXTREME' ? (
            <div className="rounded-lg border-2 border-rose-300 bg-rose-50 px-3.5 py-2.5 text-sm font-semibold text-rose-800">
              EXTREME severity — dual-auth (four-eyes) required. A second, distinct EOC_COMMANDER must co-sign before this alert reaches ACTIVE.
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={a.effectiveStatus} />} />
            <Detail label="Hazard / severity" value={<span><StatusBadge status={a.hazard} /> <StatusBadge status={a.severity} /></span>} />
            <Detail label="Districts" value={a.districts.length === 0 ? 'ALL (nationwide)' : a.districts.join(', ')} />
            <Detail label="Channels" value={a.channels.join(', ')} />
            <Detail label="Issued by" value={a.issuedBy} mono />
            <Detail label="Co-issued by" value={a.coIssuedBy ?? '—'} mono />
            <Detail label="Effective" value={formatDateTime(a.effectiveAt)} />
            <Detail label="Expires" value={formatDateTime(a.expiresAt)} />
            {a.sirenHookStatus ? <Detail label="Siren hook" value={a.sirenHookStatus} /> : null}
            {a.openGovNoticeId ? <Detail label="OpenGov Gazette notice" value={a.openGovNoticeId} mono /> : null}
          </div>
          <p className="text-slate-700">{a.description}</p>
          {a.instruction ? <p className="rounded bg-slate-50 p-2 text-xs font-semibold text-slate-700">Instruction: {a.instruction}</p> : null}
          {a.cancelReason ? <ErrorNote message={`Cancelled: ${a.cancelReason}`} /> : null}

          {a.status === 'DRAFT' && isEoc ? (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-1 text-sm font-semibold text-slate-700">Publish</div>
              <p className="mb-2 text-xs text-slate-500">
                {a.severity === 'EXTREME'
                  ? 'This will move the alert to PENDING_COSIGN, not ACTIVE — a different EOC_COMMANDER must cosign before it broadcasts.'
                  : 'This severity broadcasts immediately (SeyNotify + siren if selected) once published — no cosign required.'}
              </p>
              <p className="mb-2 text-xs text-amber-600">Requires SIGNER (T3) identity tier — hardware-key-verified sign-in.</p>
              <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/alerts/${id}/publish`))}>
                {a.severity === 'EXTREME' ? 'Publish for cosign' : 'Publish now'}
              </button>
            </div>
          ) : null}

          {a.status === 'PENDING_COSIGN' ? (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
              <div className="mb-1 text-sm font-bold text-amber-800">Awaiting second signature</div>
              <p className="mb-2 text-xs text-amber-700">
                Issued by <span className="font-mono">{a.issuedBy}</span> — a DIFFERENT EOC_COMMANDER (not that officer) must cosign for this alert to go ACTIVE and broadcast.
              </p>
              {!isCommander ? (
                <RoleNotice roles={['EOC_COMMANDER']} />
              ) : isSameOfficerAsIssuer ? (
                <ErrorNote message="You issued this alert — the backend's four-eyes control (FOUR_EYES) rejects a cosign from the same officer. Sign in as a different EOC_COMMANDER to cosign." />
              ) : (
                <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/alerts/${id}/cosign`))}>
                  Cosign as {persona?.name} — activate now
                </button>
              )}
            </div>
          ) : null}

          {a.status === 'ACTIVE' && isEoc ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-secondary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/alerts/${id}/redispatch`))}>
                Redispatch side effects
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Cancel reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <button className="btn-danger" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m36/alerts/${id}/cancel`, { reason: cancelReason || undefined }))}>
                  Cancel alert (all-clear)
                </button>
              </div>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Drafted {formatDateTime(a.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [hazard, setHazard] = useState<HazardType>('CYCLONE');
  const [severity, setSeverity] = useState<AlertSeverity>('MODERATE');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [instruction, setInstruction] = useState('');
  const [districts, setDistricts] = useState<string[]>(['ALL']);
  const [channels, setChannels] = useState<AlertChannel[]>(['PORTAL_BANNER', 'APP_BANNER']);
  const [expiresInHours, setExpiresInHours] = useState('24');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleDistrict = (d: string) => {
    if (d === 'ALL') {
      setDistricts(['ALL']);
      return;
    }
    setDistricts((prev) => {
      const withoutAll = prev.filter((x) => x !== 'ALL');
      return withoutAll.includes(d) ? withoutAll.filter((x) => x !== d) : [...withoutAll, d];
    });
  };
  const toggleChannel = (c: AlertChannel) => setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const expiresAt = new Date(Date.now() + (Number.parseFloat(expiresInHours) || 24) * 3_600_000).toISOString();
      const created = await api.post<Alert>('/api/v1/m36/alerts', {
        hazard,
        severity,
        headline,
        description,
        instruction: instruction || undefined,
        districts: districts.includes('ALL') ? [] : districts,
        channels,
        expiresAt,
      });
      onDone(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft alert');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft new alert" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {severity === 'EXTREME' ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
            EXTREME severity will require a second, distinct EOC_COMMANDER to cosign before this alert can go live.
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hazard">
            <select className="field-input" value={hazard} onChange={(e) => setHazard(e.target.value as HazardType)}>
              {HAZARDS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Severity (CAP standard)">
            <select className="field-input" value={severity} onChange={(e) => setSeverity(e.target.value as AlertSeverity)}>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Headline">
          <input className="field-input" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. Cyclone Warning — Mahé and Praslin" />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Instruction to the public (optional)">
          <input className="field-input" value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="e.g. Seek shelter immediately; avoid coastal roads" />
        </Field>
        <Field label="Districts">
          <div className="flex flex-wrap gap-1.5">
            {DISTRICTS_OR_ALL.map((d) => (
              <label
                key={d}
                className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium transition ${
                  districts.includes(d) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'
                }`}
              >
                <input type="checkbox" className="hidden" checked={districts.includes(d)} onChange={() => toggleDistrict(d)} />
                {d}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Channels">
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map((c) => (
              <label
                key={c}
                className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium transition ${
                  channels.includes(c) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'
                }`}
              >
                <input type="checkbox" className="hidden" checked={channels.includes(c)} onChange={() => toggleChannel(c)} />
                {c}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Expires in (hours)">
          <input className="field-input !w-32" type="number" min="1" value={expiresInHours} onChange={(e) => setExpiresInHours(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || headline.trim().length < 3 || description.trim().length < 10 || channels.length === 0} onClick={submit}>
            {busy ? 'Drafting…' : 'Draft alert'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
